import { useCallback, useMemo, useRef, useState } from "react"
import { InputActionMeta } from "react-select"
import debounce from "lodash/debounce"
import { OptionType } from "../../../AsyncSearchableInput/AsyncSearchableInput"
import { GetOptionsType } from "./types"

export const usePaginationSelect = (getOptions: GetOptionsType) => {
    const [page, setPage] = useState(0)
    const [options, setOptions] = useState<OptionType[]>([])
    const [inputValue, setInputValue] = useState("")
    const totalPagesRef = useRef(0)
    const getOptionsRef = useRef(getOptions)
    getOptionsRef.current = getOptions

    const debouncedSearch = useMemo(
        () =>
            debounce((text: string) => {
                getOptionsRef.current(text, 0).then(({ options, totalPages }) => {
                    totalPagesRef.current = totalPages
                    setPage(0)
                    setOptions(options)
                })
            }, 500),
        []
    )

    const handleInputChange = useCallback(
        (text: string, actionMeta: InputActionMeta) => {
            if (actionMeta.action === "input-change") {
                setInputValue(text)
                debouncedSearch(text)
            } else if (actionMeta.action === "set-value") {
                setInputValue("")
            }
        },
        [debouncedSearch]
    )

    const handleOnMenuScrollToBottom = useCallback(() => {
        const nextPage = page + 1
        if (!totalPagesRef.current || nextPage < totalPagesRef.current) {
            getOptions(inputValue, nextPage).then(({ options, totalPages }) => {
                totalPagesRef.current = totalPages
                setPage(nextPage)
                setOptions(prevState => {
                    return [...prevState, ...options]
                })
            })
        }
    }, [getOptions, page, totalPagesRef.current, inputValue])

    const handleOnMenuClose = useCallback(() => {
        setPage(0)
        totalPagesRef.current = 0
    }, [])

    const handleOnMenuOpen = useCallback(() => {
        getOptions(inputValue, 0).then(({ options, totalPages }) => {
            totalPagesRef.current = totalPages
            setOptions(options)
        })
    }, [getOptions, inputValue])

    return { options, inputValue, handleInputChange, handleOnMenuScrollToBottom, handleOnMenuClose, handleOnMenuOpen }
}
